import {AxiosInstance} from "axios";
import {isNullOrUndefined} from "util";
import Merchant from "../model/merchant/Merchant";
import {TypeOfData} from "../components/admin/merchant/MerchantConfigView";
import MerchantBranchLocation from "../model/merchant/MerchantBranchLocation";
import DictionaryCache from "../helper/DictionaryCache";
import MaterialSearch from "../model/material/MaterialSearch";
import WildcardFilter, {WildcardFilterColumnType} from "../model/workspace/WildcardFilter";
import {FilterTypeEnum} from "../model/workspace/Filter";
import {QuerySearchFilterApiDto} from "./model/rest";
import {ColumnTypeEnum} from "../model/workspace/ColumnDefinition";
import {api} from "./api";
import {WorkspaceName} from "../components/v5/table/workspace/WorkspaceContainer";
import download = require("downloadjs");
import moment = require("moment");

/**
 * Api for merchant.
 */
export default class MerchantApi {
	/**
	 * Cache for searching merchant branch locations.
	 * It prevents unnecessary request to the server.
	 */
	merchantBranchLocationsCache: DictionaryCache<MerchantBranchLocation[]>;
	/**
	 * Cache for searching materials.
	 * It prevents unnecessary request to the server.
	 */
	materialsCache: DictionaryCache<MaterialSearch[]>;

	constructor(public readonly axiosInstance: AxiosInstance) {
		this.merchantBranchLocationsCache = DictionaryCache.empty();
		this.materialsCache = DictionaryCache.empty();
	}

	/**
	 * Fetch merchants.
	 * @returns {Promise<Merchant[]>} Promise containing all merchants.
	 */
	fetchMerchants(): Promise<Merchant[]> {
		const url = `/api/web/merchants`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(Merchant.fromJson));
	}

	/**
	 * Fetch Merchant Branch Location.
	 * @returns {Promise<MerchantBranchLocation[]>} Promise containing Merchant Branch Locations.
	 */
	fetchMerchantBranchLocations(merchantId: string): Promise<MerchantBranchLocation[]> {
		const url = `/api/web/merchants/${merchantId}/branch/locations`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(MerchantBranchLocation.fromJson));
	}

	/**
	 * Create merchant action.
	 */
	createMerchant(data: Merchant): Promise<Merchant> {
		const url = `/api/web/merchants`;
		return this.axiosInstance.post(url, data.toJson()).then(resp => Merchant.fromJson(resp.data));
	}

	/**
	 * Update merchant action.
	 */
	updateMerchant(merchantId: string, data: Merchant): Promise<Merchant> {
		const url = `/api/web/merchants/${merchantId}`;
		return this.axiosInstance.put(url, {name: data.name}).then(resp => Merchant.fromJson(resp.data));
	}

	/**
	 * Update merchant sort order.
	 * @param {Merchant[]} merchants
	 * @returns {Promise<Merchant[]>}
	 */
	updateSortOrder(merchants: Merchant[]) {
		const url = `/api/web/merchants/order`;
		const merchantIds = merchants.map(m => m.id);
		return this.axiosInstance.put(url, merchantIds);
	}

	/**
	 * Download merchant file xlsx
	 * @param {Merchant} merchant
	 * @param {TypeOfData} typeOfData
	 * @returns {Promise<void>}
	 */
	downloadMerchantFile(merchant: Merchant, typeOfData: TypeOfData) {
		let url = "";
		if (typeOfData == TypeOfData.materials) {
			url = `/api/web/merchants/${merchant.id}/materials/export`;
		}
		else if (typeOfData == TypeOfData.branchLocations) {
			url = `/api/web/merchants/${merchant.id}/branch/locations/export`
		}
		const query = {responseType: "blob"};
		const typeOfDataUppercased = typeOfData.toString().toUpperCase();
		return this.axiosInstance.get(url, query).then((resp) => {
			const disposition = resp.headers['content-disposition'];
			const filename = disposition.match(/filename=(.*)/)[1];
			const merchantName = merchant.name.replace(/ /g, "_").toUpperCase();
			const optName = `MERCHANT_${typeOfDataUppercased}_${moment().format("YYYY-MM-DD_HH-MM")}_${merchantName}`;
			download(resp.data, isNullOrUndefined(filename) ? optName : filename);
		});
	}

	/**
	 * Upload merchant file xlsx
	 * @param {string} merchantId
	 * @param {File} file
	 * @param {TypeOfData} typeOfData
	 * @returns {Promise<void>}
	 */
	uploadMerchantFile(merchantId: string, file: File, typeOfData: TypeOfData) {
		let url = "";
		if (typeOfData == TypeOfData.materials) {
			url = `/api/web/merchants/${merchantId}/materials/import`
		}
		else if (typeOfData == TypeOfData.branchLocations) {
			url = `/api/web/merchants/${merchantId}/branch/locations`
		}
		const config = {
			headers: {'content-type': 'multipart/form-data'}
		};
		const formData = new FormData();
		formData.append('file', file);
		return this.axiosInstance.post(url, formData, config)
	}

	/**
	 * Download asset materials per merchant xlsx
	 * @param {Merchant} merchant
	 * @returns {Promise<void>}
	 */
	downloadAssetMaterials(merchant: Merchant) {
		const url = `/api/web/merchants/${merchant.id}/assets/materials/export`;
		const query = {responseType: "blob"};
		return this.axiosInstance.get(url, query).then((resp) => {
			const disposition = resp.headers['content-disposition'];
			const filename = disposition.match(/filename=(.*)/)[1];
			const merchantName = merchant.name.replace(/ /g, "_").toUpperCase();
			const optName = `ASSET_MATERIALS_${moment().format("YYYY-MM-DD_HH-MM")}_${merchantName}`;
			download(resp.data, isNullOrUndefined(filename) ? optName : filename);
		});
	}

	/**
	 * Upload (assign) asset materials per merchant xlsx
	 * @param {string} merchantId
	 * @param {File} file
	 * @returns {Promise<void>}
	 */
	uploadAssetMaterials(merchantId: string, file: File) {
		const url = `/api/web/merchants/${merchantId}/assets/materials/import`;
		const config = {
			headers: {'content-type': 'multipart/form-data'}
		};
		const formData = new FormData();
		formData.append('file', file);
		return this.axiosInstance.post(url, formData, config)
	}

	/**
	 * Get all merchant branch locations for given value (traverse all merchant branch location names)
	 * @param {string} value
	 */
	getAllMerchantBranchLocationsByName(value: string): Promise<MerchantBranchLocation[]> {
		return new Promise((resolve, reject) => {
			const cachedData = this.merchantBranchLocationsCache.get(value);
			if (cachedData.isDefined) {
				resolve(cachedData.get);
			}
			else {
				const url = '/api/web/merchants/branch/locations/like';
				return this.axiosInstance.get(url, {params: {name: value}}).then(
					resp => {
						const data = resp.data.map(MerchantBranchLocation.fromJson);
						this.merchantBranchLocationsCache.store(value, data);
						resolve(data)
					}).catch(reject);
			}
		});
	}

	/**
	 * Get merchant branch location for given identifier
	 * @param {string} preferredMerchantBranchLocationId
	 */
	getMerchantBranchLocationById(preferredMerchantBranchLocationId: string): Promise<MerchantBranchLocation> {
		const url = `/api/web/merchants/branch/locations/${preferredMerchantBranchLocationId}`;
		return this.axiosInstance.get(url).then(resp => MerchantBranchLocation.fromJson(resp.data))
	}

	/**
	 * Find materials by text.
	 * @param {string} merchantId.
	 * @param {string} text using to search.
	 * @returns promise with results.
	 */
	findMaterialsByText(merchantId: string, text: string): Promise<MaterialSearch[]> {
		return new Promise((resolve, reject) => {
			const cachedData = this.materialsCache.get(text);
			if (cachedData.isDefined)
				resolve(cachedData.get);
			else {
				const wildcardFilter = new WildcardFilter(text);
				const merchantFilter: QuerySearchFilterApiDto = {
					propertyName: 'merchantId',
					operatorType: FilterTypeEnum.In,
					values: [merchantId],
					columnType: ColumnTypeEnum.Merchant
				};
				api.instance.workspaceApi.fetchWorkspaceData(WorkspaceName.Materials, 1,
					[merchantFilter, wildcardFilter.toQueryJson(WildcardFilterColumnType.Materials)],
					['description,asc', 'manufacturerCode,asc', 'merchantCode,asc', 'id,asc'], 999).then(resp => {
					const data = resp.items.map(MaterialSearch.fromJson);
					this.materialsCache.store(text, data);
					resolve(data);
				}).catch(reject);
			}
		});
	}

	/**
	 * Clear materials search cache.
	 */
	clearMaterialsCache() {
		this.materialsCache = DictionaryCache.empty();
	}
}